/* eslint-disable react/no-danger */
import React, { Children } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { get } from 'lodash';

import { CoreBreadcrumbs } from 'components/core/library/breadcrumbsV2';
import Crumb from './Crumb';

import './breadcrumbs.scss';

function createCrumbSchema(crumbs = [], canonicalUrl, baseUrl) {
	const origin = canonicalUrl.startsWith('/')? baseUrl + canonicalUrl : canonicalUrl;
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		'@id': `${origin}#breadcrumbs`,
		itemListElement: crumbs
		.filter((crumb) => crumb.props.label)
		.map((crumb, index) => (
			{
				'@type': 'ListItem',
				position: index + 1,
				name: crumb.props.name || crumb.props.label,
				item: `${baseUrl}${get(crumb, 'props.href', '').split('?').shift()}`,
			}
		))
	};
}

function EnhancedBreadcrumbs({
	location = {},
	cityUrl,
	stateUrl,
	crumbs: extraCrumbs = [],
	canonicalUrl,
	...rest
}) {
	const baseUrl = useSelector((state) => state.app.baseUrl);
	const newCrumbs = [];
	const { fullState, subLocality, city } = location;
	const sub = subLocality || city;
	newCrumbs.push(
		<Crumb
			href="/"
			key="base"
			label="Home"
			name="Home"
		/>,
	);

	if (stateUrl) {
		newCrumbs.push(
			<Crumb
				key="state"
				href={stateUrl}
				isLast={!cityUrl && !extraCrumbs.length}
				label={fullState}
				className="state"
			/>,
		);
	}

	if (cityUrl) {
		newCrumbs.push(
			<Crumb
				key="sublocality"
				className="sublocality"
				href={cityUrl}
				isLast={!extraCrumbs.length}
				label={`${sub} self-storage`}
			>
				{sub} <span className="self-storage">self-storage</span>
			</Crumb>,
		);
	}

	const crumbs = [...newCrumbs, ...extraCrumbs];
	const crumbJSON = createCrumbSchema(crumbs, canonicalUrl, baseUrl);

	return (
		<Breadcrumbs
			crumbs={crumbs}
			crumbJSON={crumbJSON}
			{...rest}
		/>
	);
}

export function Breadcrumbs({
	crumbs, crumbJSON, className
}) {
	return [
		<CoreBreadcrumbs
			key="crumbs"
			className={className}
		>
			{Children.toArray(crumbs)}
		</CoreBreadcrumbs>,
		crumbJSON && (
			<script
				key="schema"
				type="application/ld+json"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify(crumbJSON)
				}}
			/>
		)
	].filter(Boolean);
}

Breadcrumbs.propTypes = {
	className: PropTypes.string,
	crumbs: PropTypes.node
};

export default EnhancedBreadcrumbs;
